<!-- components/Carruseles/CarruselPrincipalFix.vue -->
<template>
  <Carrusel
    v-if="hasValidData"
    class="hidden md:block"
    :data="carouselDataDesktop"
    :itemsPorPagina="1"
    ClaseSlide="w-full h-auto min-h-[400px] relative"
    :autoplay="true"
    :autoplayInterval="8000"
    :bucle="carouselDataDesktop.length > 1"

    :habilitarBotonesNav="true"
    customBotonesNavClass="p-1 md:p-2 bg-white hover:bg-ROJO_LEON text-GRIS_LEON hover:text-white border-2 border-GRIS_CLARO_COMPLEMENTARIO_2"
    customIconClass="text-current w-5 h-5"
    customPrevPosition="left-[4rem]"
    customNextPosition="right-[4rem]"

    :motrarPuntos="true"
    PadreDotClass="absolute bottom-4 left-1/2 transform -translate-x-1/2"
    customDotClass="w-3 h-3"
    customActiveDotClass="bg-ROJO_LEON w-6"
    customInactiveDotClass="bg-GRIS_CLARO_COMPLEMENTARIO_2">

    <template #contenido="{ items }">
      <template v-for="(item, index) in items" :key="index">
        <PanelFixImagenMasCard
          v-if="item.tipo === 'PanelFixImagenMasCard'"
          :dataPanelFix="item"
        />
        <PanelImagenMasAnchor
          v-else
          :data="{
            enlace: item.url_destino || null,
            imagen: item.url_carussel_desktop,
            alt: `Banner ${index}`,
            clase_contenedor: 'w-full h-[600px]',
            imagen_clase: 'w-full h-[600px] object-cover',
            prioridad: index === 0
          }"
        />
      </template>
    </template>
  </Carrusel>

  <Carrusel
    v-if="hasValidData"
    class="block md:hidden"
    :data="carouselDataMobile"
    :itemsPorPagina="1"
    ClaseSlide="w-full h-auto min-h-[16rem] relative"
    :autoplay="true"
    :autoplayInterval="8000"
    :bucle="carouselDataMobile.length > 1"

    :habilitarBotonesNav="false"
    customBotonesNavClass="p-1 md:p-2 bg-white hover:bg-ROJO_LEON text-GRIS_LEON hover:text-white border-2 border-GRIS_CLARO_COMPLEMENTARIO_2"
    customIconClass="text-current w-5 h-5"
    customPrevPosition="left-2"
    customNextPosition="right-2"

    :motrarPuntos="true"
    PadreDotClass="absolute bottom-6 left-1/2 transform -translate-x-1/2"
    customDotClass="w-2 h-2"
    customActiveDotClass="bg-ROJO_LEON w-6"
    customInactiveDotClass="bg-GRIS_CLARO_COMPLEMENTARIO_2">

    <template #contenido="{ items }">
      <template v-for="(item, index) in items" :key="index">
        <PanelFixImagenMasCard
          v-if="item.tipo === 'PanelFixImagenMasCard'"
          :dataPanelFix="item"
        />
        <PanelImagenMasAnchor
          v-else
          :data="{
            enlace: item.url_destino || null,
            imagen: item.url_carussel_mobile,
            alt: `Banner ${index}`,
            clase_contenedor: 'w-full h-[390px]',
            imagen_clase: 'w-full h-[390px] object-cover',
            prioridad: index === 0
          }"
        />
      </template>
    </template>
  </Carrusel>

  <div v-if="!hasValidData" class="h-[390px] md:h-[600px] bg-black flex items-center justify-center">
    <div v-if="isLoading" class="text-white">Cargando...</div>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import { aliasSistemas } from '@/constants/aliasSistemas';
import { datosLocales } from '@/constants/datosLocales';
import { NEGOCIOS } from '@/constants/negocios';
import { useStore } from 'vuex';
import Carrusel from '~/components/Carruseles/Carrusel.vue';
import PanelImagenMasAnchor from '~/components/Paneles/PanelImagenMasAnchor.vue';
import PanelFixImagenMasCard from '~/components/Paneles/PanelFixImagenMasCard.vue';

const store = useStore();
const modo_sistema = useState('modo_sistema');
const alias = computed(() => aliasSistemas[modo_sistema.value]);

const config = useRuntimeConfig();
const debug = {
  enabled: config.public.modo === 'DEV',
  log:   (...args) => debug.enabled && console.log(...args),
  error: (...args) => debug.enabled && console.error(...args),
};

const esAccesorios = computed(() => modo_sistema.value === NEGOCIOS.ACCESORIOS);
const esRevisionTecnica = computed(() => modo_sistema.value === NEGOCIOS.REVISIONTECNICA);
const esLocal = computed(() => modo_sistema.value.startsWith('local-'));

const { data: apiResponse, status } = await useFetch('/api/recursos', {
  query: computed(() => ({ action: 'getCarruselData', alias: alias.value })),
  server: !(esAccesorios.value || esRevisionTecnica.value),
  lazy: true,
  watch: [alias],
  skip: (esAccesorios.value || esRevisionTecnica.value),
});

const isLoading = computed(() => !(esAccesorios.value || esRevisionTecnica.value) && status.value === 'pending');

function buildCarouselData() {
  if (esAccesorios.value) {
    return {
      desktop: [{ url_destino: null, url_carussel_desktop: '/img/accesorios/banner_accesorios_desktop.jpg', url_carussel_mobile: '/img/accesorios/banner_accesorios_mobile.jpg' }],
      mobile:  [{ url_destino: null, url_carussel_desktop: '/img/accesorios/banner_accesorios_desktop.jpg', url_carussel_mobile: '/img/accesorios/banner_accesorios_mobile.jpg' }],
    };
  }

  if (esRevisionTecnica.value) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const calendarios2026 = {
      0:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/enero_desk.webp',     mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/enero_mobile.webp' },
      1:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/febrero_desk.webp',   mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/febrero_mobile.webp' },
      2:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/marzo_desk.webp',     mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/marzo_mobile.webp' },
      3:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/abril_desk.webp',     mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/abril_mobile.webp' },
      4:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/mayo_desk.webp',      mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/mayo_mobile.webp' },
      5:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/junio_desk.webp',     mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/junio_mobile.webp' },
      6:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/julio_desk.webp',     mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/julio_mobile.webp' },
      7:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/agosto_desk.webp',    mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/agosto_mobile.webp' },
      8:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/septiembre_desk.webp', mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/septiembre_mobile.webp' },
      9:  { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/octubre_desk.webp',   mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/octubre_mobile.webp' },
      10: { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/noviembre_desk.webp', mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/noviembre_mobile.webp' },
      11: { desk: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/diciembre_desk.webp', mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/diciembre_mobile.webp' },
    };

    const defaultBanner = {
      desk:   '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/default_desktop.webp',
      mobile: '/img/revisiontecnica/home/banner_calendarios/calendarios-2026/default_mobile.webp',
    };

    const selectedBanner = (currentYear <= 2026 && calendarios2026[currentMonth])
      ? calendarios2026[currentMonth]
      : defaultBanner;

    return {
      desktop: [{ url_destino: null, url_carussel_desktop: selectedBanner.desk, url_carussel_mobile: selectedBanner.mobile }],
      mobile:  [{ url_destino: null, url_carussel_desktop: selectedBanner.desk, url_carussel_mobile: selectedBanner.mobile }],
    };
  }

  if (apiResponse.value?.status === 'success' && apiResponse.value?.data) {
    const items = Array.isArray(apiResponse.value.data) ? apiResponse.value.data : [];

    const filtrarPorCampo = (campo) => items
      .filter(item => item?.[campo])
      .map(item => ({ ...item, url_destino: item.url_destino || null }));

    const itemsDesktop = filtrarPorCampo('url_carussel_desktop');
    const itemsMobile  = filtrarPorCampo('url_carussel_mobile');

    debug.log('[CARRUSEL] data:', { itemsDesktop, itemsMobile });

    if (itemsDesktop.length > 0) {
      if (esLocal.value && datosLocales[modo_sistema.value]) {
        const baseCard = { ...datosLocales[modo_sistema.value], tipo: 'PanelFixImagenMasCard' };

        itemsDesktop[0] = {
          ...itemsDesktop[0],
          ...baseCard,
          clase_contenedor: 'w-full h-[600px]',
          imagen_clase: 'w-full h-[600px] object-cover',
          imagen: itemsDesktop[0].url_carussel_desktop,
        };
        itemsMobile[0] = {
          ...itemsMobile[0],
          ...baseCard,
          clase_contenedor: 'w-full h-[390px]',
          imagen_clase: 'w-full h-[390px] object-cover',
          imagen: itemsMobile[0].url_carussel_mobile,
        };
      }
      return { desktop: itemsDesktop, mobile: itemsMobile };
    }
  }

  const fallback = [{
    url_destino: null,
    url_carussel_desktop: '/img/fallbacks/banner-home-desk-generico.webp',
    url_carussel_mobile:  '/img/fallbacks/banner-home-mob-generico.webp',
  }];
  return { desktop: fallback, mobile: fallback };
}

const carouselData = computed(() => buildCarouselData());
const carouselDataDesktop = computed(() => carouselData.value.desktop);
const carouselDataMobile  = computed(() => carouselData.value.mobile);

const hasValidData = computed(() =>
  Array.isArray(carouselDataDesktop.value) && carouselDataDesktop.value.length > 0
);

onMounted(() => {
  store.commit("comunes/setload_carrusel", true);
});
</script>